import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LinkSimple, Copy } from "@phosphor-icons/react";
import { formatET } from "../../lib/dateUtils";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { api, formatApiErrorDetail } from "../../lib/api";
import { WhatDoesThisMean } from "../shared/WhatDoesThisMean";

const SOURCE_LABEL = { trust: "Trust & Asset Protection", funding: "Business Funding" };

const COMMISSION_EXPLAINERS = {
  pending: "Your deal is confirmed and your commission is queued for payment once it closes.",
  approved: "IGC has approved your commission — it's scheduled for payout.",
  paid: "Your commission has been paid out. Thank you for the deal!",
  rejected: "This deal didn't qualify for a commission payout this time.",
};

const EMPTY_FUNDING_FORM = { business_name: "", contact_name: "", phone: "", email: "", amount_requested: "", notes: "" };
const EMPTY_TRUST_FORM = { client_name: "", contact_name: "", phone: "", email: "", trust_type: "", estimated_trust_value: "", notes: "" };

const PIPELINE_STAGES = ["Submitted", "Underwriting", "Approved", "Funded", "Declined"];
const PIPELINE_STAGE_COLORS = {
  Submitted: "border-igc-gold/30 text-igc-goldText/80",
  Underwriting: "border-amber-600/40 text-amber-600",
  Approved: "border-igc-gold/40 text-igc-goldText",
  Funded: "border-igc-success/50 text-igc-success",
  Declined: "border-igc-danger/40 text-igc-danger",
};
const WON_STAGES = new Set(["Won", "Closed", "Funded"]);
const LOST_STAGES = new Set(["Lost", "Dead", "Declined"]);
function mapDealToPipelineStage(stage) {
  if (WON_STAGES.has(stage)) return "Funded";
  if (LOST_STAGES.has(stage)) return "Declined";
  if (PIPELINE_STAGES.includes(stage)) return stage;
  return "Submitted";
}

export function RepDealsTab() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fundingOpen, setFundingOpen] = useState(false);
  const [trustOpen, setTrustOpen] = useState(false);
  const [fundingForm, setFundingForm] = useState(EMPTY_FUNDING_FORM);
  const [trustForm, setTrustForm] = useState(EMPTY_TRUST_FORM);
  const [links, setLinks] = useState(null);

  const fetchDeals = async () => {
    try {
      const { data } = await api.get("/affiliate/referrals");
      setDeals(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeals();
    api.get("/affiliate/referral-links").then(({ data }) => setLinks(data)).catch(() => {});
  }, []);

  const copyLink = (url) => navigator.clipboard.writeText(url).then(() => toast.success("Link copied")).catch(() => toast.error("Couldn't copy"));

  const submitFunding = async (e) => {
    e.preventDefault();
    try {
      await api.post("/affiliate/funding-lead", { ...fundingForm, amount_requested: parseFloat(fundingForm.amount_requested) || 0 });
      toast.success("Funding deal submitted to IGC");
      setFundingOpen(false);
      setFundingForm(EMPTY_FUNDING_FORM);
      fetchDeals();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
    }
  };

  const submitTrust = async (e) => {
    e.preventDefault();
    try {
      await api.post("/affiliate/trust-lead", { ...trustForm, estimated_trust_value: parseFloat(trustForm.estimated_trust_value) || 0 });
      toast.success("Trust deal submitted to IGC");
      setTrustOpen(false);
      setTrustForm(EMPTY_TRUST_FORM);
      fetchDeals();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
    }
  };

  return (
    <div data-testid="rep-deals-tab">
      {links && (
        <div className="bg-igc-surface border border-igc-gold/15 p-5 mb-6" data-testid="rep-referral-links-card">
          <p className="text-igc-cream font-serif text-lg mb-1 flex items-center gap-2"><LinkSimple size={18} weight="duotone" className="text-igc-goldText" /> Your Referral Links</p>
          <p className="text-igc-muted text-sm mb-3">Share these — every signup and deal through them is automatically credited to you.</p>
          <div className="space-y-2">
            <div className="flex items-center gap-2 bg-igc-surfaceLight px-3 py-2 text-xs" data-testid="rep-referral-link-funding">
              <span className="text-igc-muted w-16 flex-shrink-0">Funding</span>
              <span className="text-igc-cream truncate flex-1">{links.funding}</span>
              <button data-testid="rep-referral-link-funding-copy" onClick={() => copyLink(links.funding)} title="Copies your Business Funding referral link to your clipboard to share with prospects." className="text-igc-goldText hover:text-igc-gold flex-shrink-0"><Copy size={14} /></button>
            </div>
            <div className="flex items-center gap-2 bg-igc-surfaceLight px-3 py-2 text-xs" data-testid="rep-referral-link-trust">
              <span className="text-igc-muted w-16 flex-shrink-0">Trust</span>
              <span className="text-igc-cream truncate flex-1">{links.trust}</span>
              <button data-testid="rep-referral-link-trust-copy" onClick={() => copyLink(links.trust)} title="Copies your Trust & Asset Protection referral link to your clipboard to share with prospects." className="text-igc-goldText hover:text-igc-gold flex-shrink-0"><Copy size={14} /></button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <Button data-testid="rep-open-funding-lead-button" onClick={() => setFundingOpen(true)} title="Opens a form where you manually enter a funding deal you closed offline, so it gets tracked and you get credited commission for it." className="bg-igc-gold hover:bg-igc-goldHover text-black">Submit Funding Deal</Button>
        <Button data-testid="rep-open-trust-lead-button" onClick={() => setTrustOpen(true)} variant="outline" title="Opens a form where you manually enter a trust deal you closed offline, so it gets tracked and you get credited commission for it." className="border-igc-gold text-igc-goldText hover:bg-igc-gold hover:text-black">Submit Trust Deal</Button>
      </div>

      {!loading && deals.length > 0 && (
        <div className="bg-igc-surface border border-igc-gold/15 p-5 mb-6" data-testid="rep-deal-pipeline-board" title="A quick snapshot of every deal you've ever submitted, grouped by what stage it's currently at. Use this to see at a glance how many deals you have moving through the process right now.">
          <p className="text-igc-goldText text-xs uppercase tracking-widest font-medium mb-3 flex items-center">
            My Deal Pipeline
            <WhatDoesThisMean testId="rep-deal-pipeline-explainer" text="Every deal you submit moves through these 5 stages. Submitted means IGC has it and it's being processed. Underwriting means it's being reviewed by a lender. Approved means a lender said yes. Funded means the money has been disbursed — you're paid. Declined means it didn't qualify this time." />
          </p>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {PIPELINE_STAGES.map((stage) => {
              const count = deals.filter((d) => mapDealToPipelineStage(d.stage) === stage).length;
              return (
                <div key={stage} className={`border p-3 text-center ${PIPELINE_STAGE_COLORS[stage]}`} data-testid={`rep-deal-pipeline-stage-${stage.toLowerCase()}`} title={`Number of your deals currently sitting in the "${stage}" stage.`}>
                  <p className="font-serif text-xl">{count}</p>
                  <p className="text-[10px] uppercase tracking-wide mt-1">{stage}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="space-y-3">
        {!loading && deals.length === 0 && <p className="text-igc-muted text-sm" data-testid="rep-deals-empty">You haven't submitted any deals yet.</p>}
        {deals.map((r) => (
          <motion.div key={r.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="bg-igc-surface border border-igc-gold/15 p-5 flex items-center justify-between gap-4 flex-wrap" data-testid={`rep-deal-${r.id}`}>
            <div>
              <p className="text-igc-cream text-sm">{r.name} <span className="text-igc-muted text-xs">({SOURCE_LABEL[r.source] || r.source})</span></p>
              <p className="text-igc-muted text-xs">{r.contact} — {formatET(r.created_at, "MMM d, yyyy")}</p>
            </div>
            <div className="text-right">
              <span className="text-[10px] px-2 py-1 tracking-wide bg-igc-gold/10 text-igc-goldText" data-testid={`rep-deal-stage-${r.id}`} title="Where this specific deal currently sits in the pipeline — updates automatically as it moves forward.">{r.stage}</span>
              <p className={`text-xs mt-1 ${r.commission_status === "paid" ? "text-igc-success" : r.commission_status === "rejected" ? "text-igc-danger" : "text-igc-muted"}`} data-testid={`rep-deal-commission-${r.id}`}>
                {r.commission_status === "paid" && `Paid $${(r.commission_amount || 0).toLocaleString()}`}
                {r.commission_status === "approved" && `Approved $${(r.commission_amount || 0).toLocaleString()}`}
                {r.commission_status === "pending" && "Awaiting review"}
                {r.commission_status === "rejected" && "Not approved"}
                <WhatDoesThisMean testId={`rep-deal-commission-explainer-${r.id}`} text={COMMISSION_EXPLAINERS[r.commission_status] || "Your deal is being reviewed by our team."} />
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      <Dialog open={fundingOpen} onOpenChange={setFundingOpen}>
        <DialogContent className="bg-igc-surface border-igc-gold/20 text-igc-cream" data-testid="rep-funding-lead-modal">
          <DialogHeader><DialogTitle className="font-serif">Submit Funding Deal</DialogTitle></DialogHeader>
          <form onSubmit={submitFunding} className="space-y-3">
            <Input data-testid="rep-funding-lead-business-name-input" required placeholder="Business name" title="The legal or trade name of the business applying for funding." value={fundingForm.business_name} onChange={(e) => setFundingForm({ ...fundingForm, business_name: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
            <Input data-testid="rep-funding-lead-contact-name-input" required placeholder="Contact name" title="The name of the person at the business you dealt with directly." value={fundingForm.contact_name} onChange={(e) => setFundingForm({ ...fundingForm, contact_name: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
            <Input data-testid="rep-funding-lead-phone-input" required placeholder="Phone number" title="A working phone number IGC can use to reach this prospect for underwriting follow-up." value={fundingForm.phone} onChange={(e) => setFundingForm({ ...fundingForm, phone: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
            <Input data-testid="rep-funding-lead-email-input" required type="email" placeholder="Email" title="A working email address for this prospect — used for application updates and confirmations." value={fundingForm.email} onChange={(e) => setFundingForm({ ...fundingForm, email: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
            <Input data-testid="rep-funding-lead-amount-input" required type="number" min="0" placeholder="Amount requested ($)" title="How much funding this business is asking for, in dollars." value={fundingForm.amount_requested} onChange={(e) => setFundingForm({ ...fundingForm, amount_requested: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
            <Textarea data-testid="rep-funding-lead-notes-input" placeholder="Notes (optional)" title="Anything else useful for the underwriting team to know about this deal — optional." value={fundingForm.notes} onChange={(e) => setFundingForm({ ...fundingForm, notes: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
            <Button data-testid="rep-funding-lead-submit-button" type="submit" title="Sends this deal to IGC for processing and adds it to your pipeline above." className="w-full bg-igc-gold hover:bg-igc-goldHover text-black">Submit Funding Deal</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={trustOpen} onOpenChange={setTrustOpen}>
        <DialogContent className="bg-igc-surface border-igc-gold/20 text-igc-cream" data-testid="rep-trust-lead-modal">
          <DialogHeader><DialogTitle className="font-serif">Submit Trust Deal</DialogTitle></DialogHeader>
          <form onSubmit={submitTrust} className="space-y-3">
            <Input data-testid="rep-trust-lead-client-name-input" required placeholder="Client name" title="The full name of the client setting up the trust." value={trustForm.client_name} onChange={(e) => setTrustForm({ ...trustForm, client_name: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
            <Input data-testid="rep-trust-lead-contact-name-input" required placeholder="Contact name" title="The name of whoever you actually spoke with, if different from the client." value={trustForm.contact_name} onChange={(e) => setTrustForm({ ...trustForm, contact_name: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
            <Input data-testid="rep-trust-lead-phone-input" required placeholder="Phone number" title="A working phone number IGC can use to reach this client." value={trustForm.phone} onChange={(e) => setTrustForm({ ...trustForm, phone: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
            <Input data-testid="rep-trust-lead-email-input" required type="email" placeholder="Email" title="A working email address for this client." value={trustForm.email} onChange={(e) => setTrustForm({ ...trustForm, email: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
            <Input data-testid="rep-trust-lead-trust-type-input" required placeholder="Trust type" title="Which type of trust this client is setting up, for example a Basic Living Trust or a Private Trust." value={trustForm.trust_type} onChange={(e) => setTrustForm({ ...trustForm, trust_type: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
            <Input data-testid="rep-trust-lead-value-input" required type="number" min="0" placeholder="Estimated trust value ($)" title="A rough estimate of the total value of assets this client wants to protect in the trust." value={trustForm.estimated_trust_value} onChange={(e) => setTrustForm({ ...trustForm, estimated_trust_value: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
            <Textarea data-testid="rep-trust-lead-notes-input" placeholder="Notes (optional)" title="Anything else useful for the trust team to know — optional." value={trustForm.notes} onChange={(e) => setTrustForm({ ...trustForm, notes: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
            <Button data-testid="rep-trust-lead-submit-button" type="submit" title="Sends this deal to IGC for processing and adds it to your pipeline above." className="w-full bg-igc-gold hover:bg-igc-goldHover text-black">Submit Trust Deal</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
